import React, { useEffect, useState } from 'react';
import { Panel } from 'primereact/panel';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import {
  Switch, Route, useRouteMatch, useHistory,
} from 'react-router-dom';
import * as Yup from 'yup';
import gql from 'graphql-tag';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { withAccount } from '../../../components/AccountContext';
import { growlShape } from '../../../components/prop-types';
import { growlActionSuccess, growlErrors } from '../../../utils/NotificationUtil';
import AddSurveyItems from './AddSurveyItems';
import { withGrowl } from '../../../components/GrowlNotifications';
import { Loading } from '../../../components/Loading';
import AddKssTexts from './AddKssTexts';
import AddAbilityToExistingCompetency from './AddAbilityToExistingCompetency';
import CommonService from '../common';
import { INITIAL_KSS_TEXT_VALUES } from '../constants';

const ADD_ITEMBANK_SURVEY_CONTENT_CONFIG_QUERY = gql`
  query addItemBankSurveyContentConfigQuery($id: ID!) {
    addSurveyItemBankContentConfig(id: $id) {
      availableCompetencies {
        id,
        displayName
      }
      availableKssTextTypes,
      availableTextLevels {
        kssTextType,
        levels
      }
      availableSequences {
        kssTextType,
        level,
        sequences
      }
    }
  }
`;

const ADD_ITEM_BANK_SURVEY_CONTENT = gql`
  mutation addContentToExistingCompetencyMutation($input: AddContentToExistingCompetencyInput!) {
    addContentToExistingCompetency(input: $input) {
      abilityCode
    }
  }
`;

const INITIAL_CONFIG = {
  availableCompetencies: [],
  availableKssTextTypes: [],
  availableTextLevels: [],
  availableSequences: [],
  kssTextValues: INITIAL_KSS_TEXT_VALUES,
};

const AddContentToExistingCompetency = ({ currentAccount, growl }) => {
  const [submitting, setSubmitting] = useState(false);
  const [config, setConfig] = useState(INITIAL_CONFIG);
  const [addItemBankSurveyContentMutation] = useMutation(ADD_ITEM_BANK_SURVEY_CONTENT);

  const history = useHistory();
  const { path } = useRouteMatch();
  const {
    getAbilityForExistingCompetencyInput, getItemsInput, getKssTextsInput,
    getHeader, getNextKssTextValues,
  } = CommonService();

  const addItemBankSurveyContent = async (values) => {
    const abilityInput = getAbilityForExistingCompetencyInput(values);

    const itemsInput = getItemsInput(values);

    const kssTextsInput = getKssTextsInput(values);

    try {
      setSubmitting(true);

      await addItemBankSurveyContentMutation({
        variables: {
          input: {
            accountId: currentAccount,
            abilityInput,
            itemsInput,
            kssTextsInput,
          },
        },
      });
      growlActionSuccess(growl);
    } catch (mutationError) {
      growlErrors(growl, mutationError);
    } finally {
      setSubmitting(false);
    }
  };

  const { loading, error, data } = useQuery(ADD_ITEMBANK_SURVEY_CONTENT_CONFIG_QUERY,
    { variables: { id: currentAccount } });

  const { addSurveyItemBankContentConfig } = data;

  useEffect(() => {
    if (addSurveyItemBankContentConfig) {
      setConfig(previousValue => ({
        ...addSurveyItemBankContentConfig,
        kssTextValues: previousValue.kssTextValues,
      }));
    }
  }, [addSurveyItemBankContentConfig]);

  if (loading || submitting) {
    return <Loading />;
  }

  if (error) {
    growlErrors(growl, error);
  }

  const initialValues = {
    ability: {
      code: '',
      displayName: '',
      description: '',
    },
    surveyItems: [],
    kssTexts: [],
  };

  const validationSchema = Yup.object().shape({});

  const processFormValues = (surveyItemValues, values) => {
    const nextKssTextValues = getNextKssTextValues(
      surveyItemValues,
      values,
      config.availableKssTextTypes,
      config.availableTextLevels,
      config.availableSequences,
    );

    setConfig({
      ...config,
      kssTextValues: nextKssTextValues,
    });
  };

  return (
    <div className="brand-detailed-wrapper">
      <div className="wrapper p-grid">
        <div className="p-col-12">
          <Panel
            header={(
              <div className="update-translations-panel-header">
                {getHeader()}
              </div>
            )}
          >
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={values => addItemBankSurveyContent(values)}
              render={({ handleSubmit }) => (
                <Form onSubmit={handleSubmit}>
                  <Switch>
                    <Route
                      exact
                      path={`${path}`}
                      component={() => (
                        <AddAbilityToExistingCompetency
                          availableCompetencies={config.availableCompetencies}
                          currentAccount={currentAccount}
                        />
                      )}
                    />
                    <Route
                      exact
                      path={`${path}/add-survey-items`}
                      component={() => (
                        <AddSurveyItems
                          onSave={() => history
                            .push(`/accounts/${currentAccount}/add-itembank-survey-content/update/add-kss-texts`)}
                        />
                      )}
                    />
                    <Route
                      exact
                      path={`${path}/add-kss-texts`}
                      component={() => (
                        <AddKssTexts
                          currentAccount={currentAccount}
                          availableKssTextTypes={config.availableKssTextTypes}
                          availableTextLevels={config.availableTextLevels}
                          availableSequences={config.availableSequences}
                          kssTextValues={config.kssTextValues}
                          processFormValues={(surveyItemValues, values) => processFormValues(surveyItemValues, values)}
                        />
                      )}
                    />
                  </Switch>
                </Form>
              )}
            />
          </Panel>
        </div>
      </div>
    </div>
  );
};

AddContentToExistingCompetency.propTypes = {
  currentAccount: PropTypes.string.isRequired,
  growl: growlShape.isRequired,
};

export default withGrowl(withAccount(AddContentToExistingCompetency));
